/**
 * INT-01/02 — links que abrem um documento preenchido (`/criar/receita?paciente=Ana&med=…`).
 * Cada parâmetro conhecido é validado; valor inválido é ignorado, e parâmetro desconhecido vai
 * para o texto livre. O link nunca salva nem assina (INT-03): só preenche a revisão.
 */
import { z } from 'zod'

export type IntentKind = 'receita' | 'atestado' | 'exames'

export const PRESCRIPTION_FREQUENCIES = ['4h', '6h', '8h', '12h', '1d', 'dor'] as const
export const PRESCRIPTION_SIGN_KINDS = ['simples', 'controlada', 'antimicrobiano'] as const

export type PrescriptionIntent = {
  kind: 'receita'
  patient?: string
  medication?: string
  frequency?: (typeof PRESCRIPTION_FREQUENCIES)[number]
  days?: number
  continuous: boolean
  quantity?: number
  signKind?: (typeof PRESCRIPTION_SIGN_KINDS)[number]
  text?: string
  voice: boolean
  repeatLast: boolean
}

export type CertificateIntent = {
  kind: 'atestado'
  patient?: string
  days?: number
  startDate?: string
  cid?: string
  noCid: boolean
  text?: string
}

export type ExamRequestIntent = { kind: 'exames'; patient?: string; packageName?: string; text?: string }

export type ParsedIntent = PrescriptionIntent | CertificateIntent | ExamRequestIntent

const MAX_FIELD = 120
const MAX_TEXT = 1000
/** Origem do link (ícone, QR); só medição, nunca vai para a tela. */
const IGNORED_KEYS = new Set(['src'])

const shortText = z.string().trim().min(1).max(MAX_FIELD)
const flag = z.literal('1')
const intIn = (min: number, max: number) => z.coerce.number().int().min(min).max(max)
const dateKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)))
const cidCode = z.string().trim().regex(/^[A-Za-z]\d{2}(\.?\d{1,2})?$/).transform((value) => value.toUpperCase())

const FIELDS = {
  receita: {
    paciente: shortText,
    med: shortText,
    freq: z.enum(PRESCRIPTION_FREQUENCIES),
    dias: intIn(1, 365),
    continuo: flag,
    qtd: intIn(1, 999),
    tipo: z.enum(PRESCRIPTION_SIGN_KINDS),
    texto: z.string(),
    voz: flag,
    repetir: z.literal('ultima'),
  },
  atestado: {
    paciente: shortText,
    dias: intIn(1, 30),
    inicio: dateKey,
    cid: cidCode,
    semcid: flag,
    texto: z.string(),
  },
  exames: { paciente: shortText, pacote: shortText, texto: z.string() },
} as const

type Read = Record<string, unknown>

const readFields = (kind: IntentKind, params: URLSearchParams): { read: Read; extra: string[] } => {
  const schema = FIELDS[kind] as Record<string, z.ZodType>
  const read: Read = {}
  const extra: string[] = []
  for (const [key, raw] of params) {
    if (IGNORED_KEYS.has(key) || key in read) continue
    const field = schema[key]
    if (!field) {
      if (raw.trim()) extra.push(raw.trim())
      continue
    }
    const parsed = field.safeParse(raw)
    if (parsed.success) read[key] = parsed.data
  }
  return { read, extra }
}

const joinText = (text: unknown, extra: string[]): string | undefined => {
  const joined = [typeof text === 'string' ? text.trim() : '', ...extra].filter(Boolean).join(' ').slice(0, MAX_TEXT)
  return joined || undefined
}

export function parseIntentParams(tipo: string | undefined, params: URLSearchParams): ParsedIntent | null {
  if (tipo !== 'receita' && tipo !== 'atestado' && tipo !== 'exames') return null
  const { read, extra } = readFields(tipo, params)
  const text = joinText(read.texto, extra)
  const patient = read.paciente as string | undefined
  if (tipo === 'receita') {
    return {
      kind: 'receita',
      patient,
      medication: read.med as string | undefined,
      frequency: read.freq as PrescriptionIntent['frequency'],
      days: read.dias as number | undefined,
      continuous: read.continuo === '1',
      quantity: read.qtd as number | undefined,
      signKind: read.tipo as PrescriptionIntent['signKind'],
      text,
      voice: read.voz === '1',
      // INT-02: «repetir=ultima» só vale com paciente.
      repeatLast: read.repetir === 'ultima' && Boolean(patient),
    }
  }
  if (tipo === 'atestado') {
    return {
      kind: 'atestado',
      patient,
      days: read.dias as number | undefined,
      startDate: read.inicio as string | undefined,
      cid: read.semcid === '1' ? undefined : (read.cid as string | undefined),
      noCid: read.semcid === '1',
      text,
    }
  }
  return { kind: 'exames', patient, packageName: read.pacote as string | undefined, text }
}

const FREQUENCY_PHRASE: Record<NonNullable<PrescriptionIntent['frequency']>, string> = {
  '4h': 'de 4 em 4 horas',
  '6h': 'de 6 em 6 horas',
  '8h': 'de 8 em 8 horas',
  '12h': 'de 12 em 12 horas',
  '1d': '1 vez ao dia',
  dor: 'se dor',
}

/** A receita do link vira a mesma frase do ditado, para usar a busca de paciente e de remédio (INT-04). */
export function prescriptionIntentPhrase(intent: PrescriptionIntent): string {
  const parts = [
    intent.patient ? `para ${intent.patient}` : '',
    intent.medication ?? '',
    intent.frequency ? FREQUENCY_PHRASE[intent.frequency] : '',
    intent.continuous ? 'uso contínuo' : intent.days ? `por ${intent.days} dias` : '',
    intent.quantity ? `quantidade ${intent.quantity}` : '',
    intent.text ?? '',
  ]
  return parts.filter(Boolean).join(', ')
}

const INTENT_SOURCES = new Set(['pwa', 'siri', 'atalhos', 'qr', 'link', 'chat'])

/** Só medição (OBS-01): tipo, origem conhecida e quantos campos vieram preenchidos. Nunca os valores. */
export function intentAnalytics(intent: ParsedIntent, src: string | null): { kind: IntentKind; src: string; fields: number } {
  const fields = Object.entries(intent).filter(([key, value]) => key !== 'kind' && value !== undefined && value !== false).length
  const source = src?.trim().toLowerCase() ?? ''
  return { kind: intent.kind, src: INTENT_SOURCES.has(source) ? source : source ? 'outro' : 'direto', fields }
}
